import React, { useEffect, useState, useRef } from "react";
import { getDocs, collection, query, where, DocumentData } from "firebase/firestore";
import { db } from "../../firebase";
import {
  Button,
  TextInput,
  Switch,
  Group,
  Select,
  Text,
  Tooltip,
  ActionIcon,
  Alert,
  ScrollArea,
  Box,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import FormSelectOptionsManager from "./form-select-options-manager";
import StaticOptionsEditor from "./static-options-editor";
import FirestoreOptionsPreview from "./firestore-options-preview";
import { ArrowBackUp, Click, Eye, Plus, InfoCircle, Check } from "tabler-icons-react";
import { FIELD_TYPE_OPTIONS } from "../../hooks/flexFormUtils";
import {
  FormFieldConfig,
  FlexFormTemplate,
  FormFieldValidation,
  FormFieldCondition,
  FormFieldDependency,
  FlexFormSelectOption,
  OptionsSource,
  FlexFormInputType,
} from "@captiva/shared-types";

interface FieldEditorProps {
  field: FormFieldConfig & { templateId?: string };
  onSave: (field: FormFieldConfig) => void;
  onCancel: () => void;
  allTemplates: FlexFormTemplate[];
  companyId: string;
  fields: FormFieldConfig[];
  isPublicCaptureEnabled?: boolean;
}

export default function FieldEditor({
  field,
  onSave,
  onCancel,
  allTemplates,
  companyId,
  fields,
  isPublicCaptureEnabled = false,
}: FieldEditorProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const inputSize = isMobile ? "md" : "sm";
  const [validation, setValidation] = useState<FormFieldValidation>({ required: true, ...field.validation } as FormFieldValidation);
  const [displayIf, setDisplayIf] = useState<FormFieldCondition[]>((field.displayIf as FormFieldCondition[]) || []);
  const [dependencies, setDependencies] = useState<FormFieldDependency[]>((field.dependencies as FormFieldDependency[]) || []);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const advancedRef = useRef<HTMLDivElement>(null);

  const toggleAdvanced = () => {
    setAdvancedOpen((prev) => {
      const next = !prev;
      if (next) {
        setTimeout(() => {
          advancedRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 120);
      }
      return next;
    });
  };
  const [id] = useState(field.id || crypto.randomUUID());
  const [label, setLabel] = useState(field.label || "");
  const [keyValue, setKeyValue] = useState(field.key || "");
  const [type, setType] = useState<FlexFormInputType>((field.type as FlexFormInputType) || "text");
  const usesOptionsSource = ["select", "multiSelect", "checklist", "checklistWithNotes"].includes(type);
  const [optionsType, setOptionsType] = useState<"static" | "firestore">(
    (field.optionsSource?.type as "static" | "firestore") || "static"
  );
  const [staticOptions, setStaticOptions] = useState<FlexFormSelectOption[]>(
    field.optionsSource?.type === "static" ? field.optionsSource.options || [] : []
  );
  const [optionsDocs, setOptionsDocs] = useState<DocumentData[]>([]);

  // Use Firestore document id for referencing options sets
  const [firestoreDocId, setFirestoreDocId] = useState<string>(
    field.optionsSource?.type === "firestore"
      ? (field.optionsSource as any).id || ""
      : ""
  );
  const [optionsManagerOpen, setOptionsManagerOpen] = useState(false);
  const [hidden, setHidden] = useState(field.hidden || false);
  const [readOnly, setReadOnly] = useState(field.readOnly || false);
  const [canPrepopulate, setCanPrepopulate] = useState(field.canPrepopulate || false);
  const [includeInPdf, setIncludeInPdf] = useState(field.includeInPdf ?? true);
  const [includeMetadataInPdf, setIncludeMetadataInPdf] = useState(field.includeMetadataInPdf ?? false);
  const [checklistNoteInputType, setChecklistNoteInputType] = useState<"text" | "number">(
    (field.checklistNoteInputType as "text" | "number") || "text"
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    const q = query(
      collection(db, "flexFormSelectOptions"),
      where("companyId", "==", companyId)
    );
    getDocs(q).then((snapshot) => {
      setOptionsDocs(
        snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
    });
  }, [companyId, optionsManagerOpen]);

  // defaultValue state: string | number | option value(s)
  const [defaultValue, setDefaultValue] = useState<any>(
    type === "number"
      ? typeof field.defaultValue === "number"
        ? field.defaultValue
        : ""
      : type === "multiSelect" || type === "checklist" || type === "checklistWithNotes"
        ? Array.isArray(field.defaultValue)
          ? field.defaultValue
          : []
        : field.defaultValue ?? ""
  );

  const save = () => {
    setErrorMessage(null);
    if (!label.trim() || !keyValue.trim()) {
      setErrorMessage("Label and key are required.");
      return;
    }
    if (usesOptionsSource) {
      if (optionsType === "static" && staticOptions.length === 0) {
        setErrorMessage("Please add at least one option for static options.");
        return;
      }
      if (optionsType === "firestore" && !firestoreDocId.trim()) {
        setErrorMessage("Please select an options set for dynamic options.");
        return;
      }
    }
    let optionsSource: OptionsSource | undefined;
    if (usesOptionsSource) {
      if (optionsType === "static") {
        optionsSource = { type: "static", options: staticOptions };
      } else {
        optionsSource = {
          type: "firestore" as any,
          collection: "flexFormSelectOptions",
          id: firestoreDocId,
        } as any;
      }
    }
    let processedDefaultValue = defaultValue;
    if (type === "number") {
      processedDefaultValue =
        defaultValue === "" ? null : Number(defaultValue);
    } else if (type === "multiSelect" || type === "checklist" || type === "checklistWithNotes") {
      processedDefaultValue = Array.isArray(defaultValue)
        ? defaultValue
        : defaultValue
          ? [defaultValue]
          : [];
    } else {
      processedDefaultValue = defaultValue;
    }
    const fieldToSave: FormFieldConfig = {
      ...field,
      id,
      label,
      key: keyValue,
      type,
      hidden,
      readOnly,
      canPrepopulate,
      includeInPdf,
      includeMetadataInPdf,
      ...(type === "checklistWithNotes" ? { checklistNoteInputType } : {}),
      defaultValue: processedDefaultValue,
      validation,
    };
    if (displayIf.length) {
      fieldToSave.displayIf = displayIf;
    } else {
      fieldToSave.displayIf = null;
    }
    if (dependencies.length) {
      fieldToSave.dependencies = dependencies;
    } else {
      fieldToSave.dependencies = null;
    }
    if (optionsSource) {
      fieldToSave.optionsSource = optionsSource as any;
    } else {
      fieldToSave.optionsSource = null;
    }
    onSave(fieldToSave);
  };

  // Helper to get options for select/multiselect
  const getOptions = () => {
    if (optionsType === "static") {
      return staticOptions.map((opt) => ({
        value: String(opt.value),
        label: opt.label,
      }));
    }
    if (optionsType === "firestore") {
      const selectedOptionsDocument = optionsDocs.find(
        (doc) => doc.id === firestoreDocId
      );
      return selectedOptionsDocument
        ? (selectedOptionsDocument.options as FlexFormSelectOption[]).map((opt) => ({
            value: String(opt.value),
            label: opt.label,
          }))
        : [];
    }
    return [];
  };

  const getDependentFieldOptions = (fieldId: string) => {
    const fieldObj = fields.find((f) => f.id === fieldId);
    if (!fieldObj) return [];
    if (fieldObj.optionsSource?.type === "static") {
      return (fieldObj.optionsSource.options || []).map((opt) => ({
        value: String(opt.value),
        label: opt.label,
      }));
    }
    if (fieldObj.optionsSource?.type === "firestore") {
      const selectedOptionsDocument = optionsDocs.find(
        (doc) => doc.id === (fieldObj.optionsSource as any).id
      );
      return selectedOptionsDocument
        ? (selectedOptionsDocument.options as FlexFormSelectOption[]).map((opt) => ({
            value: String(opt.value),
            label: opt.label,
          }))
        : [];
    }
    return [];
  };

  const getValidOperators = (fieldType: string | undefined) => {
    if (!fieldType) return [];
    switch (fieldType) {
      case "text":
      case "barcode":
      case "nfc":
        return ["equals", "notEquals", "in", "notIn"];
      case "number":
        return ["equals", "notEquals", "greaterThan", "lessThan"];
      case "boolean":
        return ["equals", "notEquals"];
      case "select":
        return ["equals", "notEquals"];
      case "multiSelect":
      case "checklist":
      case "checklistWithNotes":
      case "multiBarcode":
        return ["in", "notIn"];
      default:
        return [];
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", flex: 1, minHeight: 0, overflow: "hidden" }}>
      {errorMessage && (
        <Alert color="red" mb="sm" title="Error" style={{ flexShrink: 0 }}>
          {errorMessage}
        </Alert>
      )}
      <ScrollArea
        style={{ flex: isMobile ? 1 : "0 1 auto", minHeight: 0 }}
        styles={{
          viewport: {
            maxHeight: isMobile ? "100%" : "calc(85vh - 140px)",
            overflowX: "hidden",
          },
          scrollbar: {
            '&[data-orientation="horizontal"]': {
              display: "none",
            },
          },
        }}
        offsetScrollbars
        type="auto"
      >
        <div style={{ paddingRight: isMobile ? 4 : 8, paddingBottom: 16 }}>
        <Group grow spacing="md" mb="sm" align="flex-end" style={{ flexWrap: 'wrap' }}>
          <TextInput
            size={inputSize}
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            error={!label.trim() ? "Label is required" : undefined}
          />
          <TextInput
            size={inputSize}
            label="Key"
            value={keyValue}
            onChange={(e) => setKeyValue(e.target.value)}
            required
            error={!keyValue.trim() ? "Key is required" : undefined}
          />
        </Group>
        <Select
          size={inputSize}
          label="Type"
          value={type}
          onChange={(val) => {
            if (val) {
              const nextType = val as FlexFormInputType;
              setType(nextType);
              // Reset defaultValue when type changes
              if (nextType === "number") setDefaultValue("");
              else if (nextType === "multiSelect" || nextType === "checklist" || nextType === "checklistWithNotes") setDefaultValue([]);
              else setDefaultValue("");
            }
          }}
          data={FIELD_TYPE_OPTIONS}
          required
          mb="sm"
        />
        {isPublicCaptureEnabled && ["photo", "multiPhoto", "gps", "barcode", "multiBarcode", "nfc"].includes(type) && (
          <Group spacing={6} align="flex-start" noWrap mb="sm">
            <InfoCircle size={14} color="#e67e22" style={{ flexShrink: 0, marginTop: 2 }} />
            <Text size="xs" color="dimmed">
              <b>Online Capture Note:</b> Requires device hardware (camera, GPS, or NFC); falls back to manual file/text entry on desktop browsers if unavailable.
            </Text>
          </Group>
        )}
        <Switch
          color="cap-blue"
          label="Hidden"
          checked={hidden}
          onChange={(e) => setHidden(e.target.checked)}
          mb="sm"
        />
        <Switch
          color="cap-blue"
          label="Read Only"
          checked={readOnly}
          onChange={(e) => setReadOnly(e.target.checked)}
          mb="sm"
        />
        <Switch
          color="cap-blue"
          label="Include in PDF"
          checked={includeInPdf}
          onChange={(e) => setIncludeInPdf(e.target.checked)}
          mb="sm"
        />
        {(type === "select" || type === "multiSelect") && (
          <Switch
            color="cap-blue"
            label="Include Metadata in PDF"
            checked={includeMetadataInPdf}
            onChange={(e) => setIncludeMetadataInPdf(e.target.checked)}
            mb="sm"
          />
        )}
        {!["photo", "multiPhoto", "gps", "barcode", "multiBarcode", "nfc"].includes(type) && (
          <Switch
            color="cap-blue"
            label={
              <Group spacing={4} align="center">
                <Text size="sm">Can Prepopulate</Text>
                <Tooltip label="When enabled, this field can be automatically filled with the value from the user's most recent submission of this form.">
                  <ActionIcon size="xs" variant="transparent" color="gray">
                    <InfoCircle size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            }
            checked={canPrepopulate}
            onChange={(e) => setCanPrepopulate(e.target.checked)}
            mb="sm"
          />
        )}
        <Switch
          color="cap-blue"
          mb="sm"
          label="Required"
          checked={!!validation.required}
          onChange={(e) => {
            setValidation((v) => ({ ...v, required: e.target.checked }));
          }}
        />
        {type === "checklistWithNotes" && (
          <Select
            size={inputSize}
            label="Checklist note input"
            value={checklistNoteInputType}
            onChange={(val) => {
              if (val) setChecklistNoteInputType(val as "text" | "number");
            }}
            data={[
              { value: "text", label: "Text" },
              { value: "number", label: "Number" },
            ]}
            mb="sm"
            style={{ maxWidth: isMobile ? "100%" : 300 }}
          />
        )}
        {usesOptionsSource && (
          <>
            <Select
              size={inputSize}
              label="Options Source"
              value={optionsType}
              onChange={(val) => {
                if (val) setOptionsType(val as "static" | "firestore");
              }}
              data={[
                { value: "static", label: "Static" },
                { value: "firestore", label: "Dynamic (options set)" },
              ]}
              mb="sm"
            />
            {optionsType === "firestore" && (
              <Button
                size={inputSize}
                fullWidth
                color="cap-blue"
                variant="outline"
                leftIcon={<Click size={16} />}
                onClick={() => setOptionsManagerOpen(true)}
                mb="md"
              >
                Select options set
              </Button>
            )}
            {optionsType === "static" ? (
              <div
                style={{
                  border: "1px solid lightgray",
                  borderRadius: 4,
                  padding: "8px",
                }}
              >
                <Text size="sm" mb={4}>
                  Static options
                </Text>
                <StaticOptionsEditor
                  options={staticOptions}
                  setOptions={setStaticOptions}
                />
              </div>
            ) : null}
            {optionsType === "firestore" && firestoreDocId && (
              <FirestoreOptionsPreview
                firestoreDocId={firestoreDocId}
                companyId={companyId}
              />
            )}
            <FormSelectOptionsManager
              open={optionsManagerOpen}
              onClose={() => setOptionsManagerOpen(false)}
              companyId={companyId}
              onSelect={(docObj) => {
                setFirestoreDocId(docObj.id);
                setOptionsManagerOpen(false);
              }}
              selectedField={firestoreDocId}
              allTemplates={allTemplates}
              editingTemplateId={field.templateId || ""}
            />
          </>
        )}
        {(type === "text" || type === "number" || type === "boolean" || type === "select" || type === "multiSelect") && (
          type === "number" ? (
            <TextInput
              size={inputSize}
              label="Default Value"
              type="number"
              value={defaultValue}
              style={{ maxWidth: isMobile ? "100%" : 300 }}
              onChange={(e) => setDefaultValue(e.target.value)}
              mb="sm"
            />
          ) : type === "boolean" ? (
            <Switch
              size={inputSize}
              color="cap-blue"
              label="Default Value"
              checked={!!defaultValue}
              style={{ maxWidth: isMobile ? "100%" : 300 }}
              onChange={(e) => setDefaultValue(e.currentTarget.checked)}
              mb="sm"
            />
          ) : type === "select" ? (
            <Select
              size={inputSize}
              label="Default Value"
              value={defaultValue}
              onChange={setDefaultValue}
              data={getOptions()}
              mb="sm"
              style={{ maxWidth: isMobile ? "100%" : 300 }}
              clearable
            />
          ) : type === "multiSelect" ? (
            <Select
              size={inputSize}
              label="Default Value"
              value={defaultValue}
              onChange={setDefaultValue}
              data={getOptions()}
              style={{ maxWidth: isMobile ? "100%" : 300 }}
              mb="sm"
              clearable
              multiple
            />
          ) : (
            <TextInput
              size={inputSize}
              label="Default Value"
              style={{ maxWidth: isMobile ? "100%" : 300 }}
              value={defaultValue}
              onChange={e => setDefaultValue(e.target.value)}
              mb="sm"
            />
          )
        )}
        {advancedOpen && (
          <div ref={advancedRef} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 16, marginTop: 16 }}>
            <Text weight={500} mb={8}>Advanced Field Settings</Text>
            <Group mb="sm" spacing={8} align="start" style={{ display: 'flex', flexDirection: 'column', alignContent: 'flex-start' }}>
              {!(
                type === 'date' ||
                type === 'datetime' ||
                type === 'time' ||
                type === 'signature' ||
                type === 'photo' ||
                type === 'multiPhoto'
              ) && (
                type === 'select' || type === 'multiSelect' ? (
                  <Select
                    size={inputSize}
                    label="Expected Value"
                    value={validation.expectedValue ?? (type === 'multiSelect' ? [] : '')}
                    onChange={val => setValidation(v => ({ ...v, expectedValue: val }))}
                    data={getOptions()}
                    style={{ maxWidth: isMobile ? "100%" : 180 }}
                    clearable
                    multiple={type === 'multiSelect'}
                  />
                ) : type === 'multiBarcode' ? (
                  <Select
                    size={inputSize}
                    label="Expected Value"
                    value={validation.expectedValue ?? []}
                    onChange={val => setValidation(v => ({ ...v, expectedValue: val }))}
                    data={[]}
                    style={{ maxWidth: isMobile ? "100%" : 180 }}
                    clearable
                    multiple
                  />
                ) : type === 'number' ? (
                  <TextInput
                    size={inputSize}
                    label="Expected Value"
                    type="number"
                    value={validation.expectedValue ?? ''}
                    onChange={e => setValidation(v => ({ ...v, expectedValue: e.target.value === '' ? null : Number(e.target.value) }))}
                    style={{ maxWidth: isMobile ? "100%" : 180 }}
                  />
                ) : type === 'boolean' ? (
                  <Switch
                    size={inputSize}
                    color="cap-blue"
                    label="Expected Value"
                    checked={!!validation.expectedValue}
                    onChange={e => setValidation(v => ({ ...v, expectedValue: e.currentTarget.checked }))} />
                ) : (
                  <TextInput size={inputSize} label="Expected Value" value={validation.expectedValue ?? ''} onChange={e => setValidation(v => ({ ...v, expectedValue: e.target.value }))} style={{ maxWidth: isMobile ? "100%" : 180 }} />
                )
              )}
              {type === 'text' && (
                <TextInput size={inputSize} label="Regex" value={validation.regex || ''} onChange={e => setValidation(v => ({ ...v, regex: e.target.value }))} style={{ maxWidth: isMobile ? "100%" : 180 }} />
              )}
              {type === 'number' && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <TextInput
                    size={inputSize}
                    label="Min"
                    type="number"
                    value={validation.range?.min ?? ''}
                    onChange={e => {
                      const val = e.target.value === '' ? undefined : Number(e.target.value);
                      setValidation(v => {
                        const max = v.range?.max;
                        if (val === undefined && max === undefined) {
                          const { range, ...rest } = v;
                          return rest;
                        }
                        return {
                          ...v,
                          range: { min: val as number, max: max as number }
                        };
                      });
                    }}
                    style={{ maxWidth: isMobile ? "50%" : 100 }}
                  />
                  <TextInput
                    size={inputSize}
                    label="Max"
                    type="number"
                    value={validation.range?.max ?? ''}
                    onChange={e => {
                      const val = e.target.value === '' ? undefined : Number(e.target.value);
                      setValidation(v => {
                        const min = v.range?.min;
                        if (val === undefined && min === undefined) {
                          const { range, ...rest } = v;
                          return rest;
                        }
                        return {
                          ...v,
                          range: { min: min as number, max: val as number }
                        };
                      });
                    }}
                    style={{ maxWidth: isMobile ? "50%" : 100 }}
                  />
                </div>
              )}
            </Group>
            {/* Conditional Display */}
            <Text weight={500} size="sm" mt={16} mb={4}>Conditional display (Show this field if...)</Text>
            {displayIf.map((cond, idx) => (
              <Group key={idx} mb={isMobile ? "md" : 4} ml={isMobile ? 0 : 8} spacing={8} align="end" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-start', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                <Select
                  size={inputSize}
                  label="Field"
                  value={cond.fieldId}
                  onChange={val => {
                    if (val) {
                      const newField = fields.find(f => f.id === val);
                      const validOps = newField ? getValidOperators(newField.type) : [];
                      const defaultOp = validOps.length > 0 ? validOps[0] : '';
                      setDisplayIf(arr => arr.map((c, i) => i === idx ? { ...c, fieldId: val, operator: defaultOp as any } : c));
                    }
                  }}
                  data={fields.filter(f => f.id !== id && !['photo', 'multiPhoto', 'gps', 'signature', 'datetime', 'time', 'date', 'barcode', 'multiBarcode'].includes(f.type)).map(f => ({ value: f.id, label: f.label }))}
                  style={{ minWidth: 100, flex: isMobile ? '1 1 120px' : undefined }}
                />
                <Select
                  size={inputSize}
                  label="Operator"
                  value={cond.operator}
                  onChange={val => {
                    if (val) setDisplayIf(arr => arr.map((c, i) => i === idx ? { ...c, operator: val as any } : c));
                  }}
                  data={getValidOperators(fields.find(f => f.id === cond.fieldId)?.type).map(op => ({ value: op, label: op }))}
                  style={{ minWidth: 100, flex: isMobile ? '1 1 100px' : undefined }}
                />
                {(() => {
                  const selectedField = fields.find(f => f.id === cond.fieldId);
                  if (selectedField) {
                    if (selectedField.type === 'select' || selectedField.type === 'multiSelect' || selectedField.type === 'checklist' || selectedField.type === 'checklistWithNotes') {
                      return (
                        <Select
                          size={inputSize}
                          label="Value"
                          value={cond.value ?? ''}
                          onChange={val => setDisplayIf(arr => arr.map((c, i) => i === idx ? { ...c, value: val } : c))}
                          data={getDependentFieldOptions(cond.fieldId)}
                          style={{ minWidth: 100, flex: isMobile ? '1 1 120px' : undefined }}
                          clearable
                        />
                      );
                    } else if (selectedField.type === 'number') {
                      return (
                        <TextInput
                          size={inputSize}
                          label="Value"
                          type="number"
                          value={cond.value ?? ''}
                          onChange={e => setDisplayIf(arr => arr.map((c, i) => i === idx ? { ...c, value: e.target.value === '' ? null : Number(e.target.value) } : c))}
                          style={{ minWidth: 100, flex: isMobile ? '1 1 100px' : undefined }}
                        />
                      );
                    } else if (selectedField.type === 'boolean') {
                      return (
                        <Switch
                          size={inputSize}
                          label="Value"
                          color="cap-blue"
                          checked={!!cond.value}
                          onChange={e => setDisplayIf(arr => arr.map((c, i) => i === idx ? { ...c, value: e.currentTarget.checked } : c))}
                          style={{ minWidth: 100, marginTop: 8 }}
                        />
                      );
                    } else if (selectedField.type === 'multiBarcode') {
                      return (
                        <Select
                          size={inputSize}
                          label="Value"
                          value={cond.value ?? []}
                          onChange={val => setDisplayIf(arr => arr.map((c, i) => i === idx ? { ...c, value: val } : c))}
                          data={[]}
                          style={{ minWidth: 100, flex: isMobile ? '1 1 120px' : undefined }}
                          clearable
                          multiple
                        />
                      );
                    }
                  }
                  return (
                    <TextInput
                      size={inputSize}
                      label="Value"
                      value={cond.value ?? ''}
                      onChange={e => setDisplayIf(arr => arr.map((c, i) => i === idx ? { ...c, value: e.target.value } : c))}
                      style={{ minWidth: 100, flex: isMobile ? '1 1 100px' : undefined }}
                    />
                  );
                })()}
                <Button
                  size={isMobile ? "md" : inputSize}
                  color="red"
                  variant="outline"
                  style={isMobile ? { minHeight: 44, minWidth: 44, marginTop: 4 } : undefined}
                  onClick={() => setDisplayIf(arr => arr.filter((_, i) => i !== idx))}
                >
                  Remove
                </Button>
              </Group>
            ))}
            <Button
              size={inputSize}
              variant="outline"
              color="cap-blue"
              leftIcon={<Plus size={16} />}
              ml={isMobile ? 0 : 8}
              mt={isMobile ? 14 : 4}
              style={isMobile ? { minHeight: 44 } : undefined}
              onClick={() => setDisplayIf(arr => [...arr, { fieldId: '', operator: 'equals', value: '' }])}
            >
              Condition
            </Button>
            {/* Downstream effects */}
            {!["photo", "multiPhoto", "gps", "signature", "datetime", "time", "date", "barcode", "multiBarcode"].includes(field.type) && (
              <>
                <Text weight={500} size="sm" mt={16} mb={4}>Downstream effects (change other field values when field value changes)</Text>
                {dependencies.map((dep, idx) => (
                  <Group key={idx} mb={isMobile ? "md" : 4} ml={isMobile ? 0 : 8} spacing={8} align="end" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-start', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                    <Select
                      size={inputSize}
                      label="Field"
                      value={dep.fieldId}
                      onChange={val => {
                        if (val) setDependencies(arr => arr.map((d, i) => i === idx ? { ...d, fieldId: val } : d));
                      }}
                      data={fields
                        .filter(f => f.id !== id && !["photo", "multiPhoto", "gps", "signature", "barcode", "multiBarcode", "nfc"].includes(f.type))
                        .map(f => ({ value: f.id, label: f.label }))}
                      style={{ minWidth: 120, flex: isMobile ? '1 1 120px' : undefined }}
                    />
                    <Select
                      size={inputSize}
                      label="Action"
                      value={dep.action}
                      onChange={val => {
                        if (val) setDependencies(arr => arr.map((d, i) => i === idx ? { ...d, action: val as any } : d));
                      }}
                      data={[{ value: 'reset', label: 'reset' }, { value: 'update', label: 'update' }]}
                      style={{ minWidth: 100, flex: isMobile ? '1 1 100px' : undefined }}
                    />
                    {(() => {
                      const selectedField = fields.find(f => f.id === dep.fieldId);
                      if (
                        selectedField &&
                        dep.action === 'update' &&
                        !["photo", "multiPhoto", "gps", "signature", "datetime", "time", "date"].includes(selectedField.type)
                      ) {
                        if (selectedField.type === 'select' || selectedField.type === 'multiSelect') {
                          return (
                            <Select
                              size={inputSize}
                              label="Value (optional)"
                              value={dep.value ?? (selectedField.type === 'multiSelect' ? [] : '')}
                              onChange={val => setDependencies(arr => arr.map((d, i) => i === idx ? { ...d, value: val } : d))}
                              data={getDependentFieldOptions(dep.fieldId)}
                              style={{ minWidth: 120, flex: isMobile ? '1 1 120px' : undefined }}
                              clearable
                              multiple={selectedField.type === 'multiSelect'}
                            />
                          );
                        } else if (selectedField.type === 'number') {
                          return (
                            <TextInput
                              size={inputSize}
                              label="Value (optional)"
                              type="number"
                              value={dep.value ?? ''}
                              onChange={e => setDependencies(arr => arr.map((d, i) => i === idx ? { ...d, value: e.target.value === '' ? null : Number(e.target.value) } : d))}
                              style={{ minWidth: 120, flex: isMobile ? '1 1 120px' : undefined }}
                            />
                          );
                        } else if (selectedField.type === 'boolean') {
                          return (
                            <Switch
                              size={inputSize}
                              label="Value (optional)"
                              color="cap-blue"
                              checked={!!dep.value}
                              onChange={e => setDependencies(arr => arr.map((d, i) => i === idx ? { ...d, value: e.currentTarget.checked } : d))}
                              style={{ minWidth: 120, marginTop: 8 }}
                            />
                          );
                        } else if (selectedField.type === 'multiBarcode') {
                          return (
                            <Select
                              size={inputSize}
                              label="Value (optional)"
                              value={dep.value ?? []}
                              onChange={val => setDependencies(arr => arr.map((d, i) => i === idx ? { ...d, value: val } : d))}
                              data={[]}
                              style={{ minWidth: 120, flex: isMobile ? '1 1 120px' : undefined }}
                              clearable
                              multiple
                            />
                          );
                        }
                        return (
                          <TextInput
                            size={inputSize}
                            label="Value (optional)"
                            value={dep.value ?? ''}
                            onChange={e => setDependencies(arr => arr.map((d, i) => i === idx ? { ...d, value: e.target.value } : d))}
                            style={{ minWidth: 120, flex: isMobile ? '1 1 120px' : undefined }}
                          />
                        );
                      }
                    })()}
                    <Button
                      size={isMobile ? "md" : inputSize}
                      color="red"
                      variant="outline"
                      style={isMobile ? { minHeight: 44, minWidth: 44, marginTop: 4 } : undefined}
                      onClick={() => setDependencies(arr => arr.filter((_, i) => i !== idx))}
                    >
                      Remove
                    </Button>
                  </Group>
                ))}
                <Button
                  size={inputSize}
                  color="cap-blue"
                  variant="outline"
                  leftIcon={<Plus size={16} />}
                  ml={isMobile ? 0 : 8}
                  mt={isMobile ? 14 : 4}
                  style={isMobile ? { minHeight: 44 } : undefined}
                  onClick={() => setDependencies(arr => [...arr, { fieldId: '', action: 'reset', value: '' }])}
                >
                  Effect
                </Button>
              </>
            )}
          </div>
        )}
        </div>
      </ScrollArea>
      <Box
        pt="sm"
        mt="auto"
        sx={(theme) => ({
          flexShrink: 0,
          borderTop: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,
          backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
          zIndex: 10,
          paddingBottom: `calc(${theme.spacing.xs}px + env(safe-area-inset-bottom, 0px))`,
        })}
      >
        <Group position="apart" style={{ width: "100%", gap: isMobile ? 8 : 12 }}>
          <Button
            variant="outline"
            size={inputSize}
            color="cap-blue"
            leftIcon={advancedOpen ? <ArrowBackUp size={16} /> : <Eye size={16} />}
            onClick={toggleAdvanced}
            style={isMobile ? { minHeight: 44 } : undefined}
          >
            {isMobile ? "Advanced" : "Advanced settings"}
          </Button>
          <Group position="right" spacing={isMobile ? "md" : "xs"}>
            <Button
              variant="default"
              color="gray"
              size={inputSize}
              leftIcon={<ArrowBackUp size={16} />}
              onClick={onCancel}
              style={{ minHeight: isMobile ? 44 : 36 }}
            >
              Cancel
            </Button>
            <Button
              color="cap-navy"
              size={inputSize}
              leftIcon={<Check size={16} />}
              onClick={save}
              disabled={!label.trim()}
              style={{ minHeight: isMobile ? 44 : 36 }}
            >
              Done
            </Button>
          </Group>
        </Group>
      </Box>
    </div>
  );
}